import moment from 'moment';
import { throttle } from 'lodash';

import { UUID } from '@shared/utils/uuid';

import { DISPATCHER_CONSTANTS, DISPATCHER_LAYOUT_REFRESH_EVENT } from './dispatcher_constants';
import { updateDispatchGroupings } from './dispatcher_layout';
import { positionForOrder, toPercentageStyle } from './dispatcher_position';
import { DispatcherData, DispatcherDispatchRecord, DispatcherOrderRecord, DispatcherRegion } from './dispatcher_types';
import { DispatcherResources } from './hooks/use_dispatcher_resources';

const isNotWarehouseOrder = (order: DispatcherOrderRecord) =>
  order.address?.addressable_type !== 'Warehouse' || order.type === 'restock' || order.service_type === 'drop_off';

const isWarehouseOrder = (order: DispatcherOrderRecord) => !isNotWarehouseOrder(order);

export const MISSING_ESTIMATED_DURATION_MESSAGE = 'Please estimate this order first!';

export type DispatcherDropZoneStyle = {
  top: string;
  bottom: string;
  height: string;
  display: string;
};

export type DispatcherRegionConfig = DispatcherRegion & {
  id: number;
  default_warehouse_id?: number;
  default_depot_id?: number;
};

export const cloneDispatcherData = (data: DispatcherData): DispatcherData => {
  const ordersById = new Map(data.orders.map((order) => [order.id, { ...order }]));

  const dispatches = data.dispatches.map((dispatch) => ({
    ...dispatch,
    orders: dispatch.orders.map((order) => ordersById.get(order.id) ?? { ...order }),
  }));

  const orders = Array.from(ordersById.values());
  const nonRestockChildOrders = orders.filter((order) => order.parent_id && order.type !== 'restock');

  return {
    ...data,
    dispatches,
    orders,
    nonRestockChildOrders,
  };
};

/** Layout rebuilds shallow-copy dispatches; match by id/mapID for editor mutations. */
export const resolveEditorDispatch = (
  dispatches: DispatcherDispatchRecord[],
  target: DispatcherDispatchRecord,
): DispatcherDispatchRecord | undefined =>
  dispatches.find(
    (entry) =>
      entry === target ||
      (target.id != null && entry.id === target.id) ||
      (target.mapID != null && entry.mapID === target.mapID),
  );

/** When false (post T+3 handoff), all routes are editable regardless of auto_dispatch_enabled. */
export const isRouteEditable = (dispatch: DispatcherDispatchRecord, routeLockActive = true, manageMode = false) =>
  !routeLockActive || manageMode || !dispatch.id || dispatch.auto_dispatch_enabled === false;

export const isSelectionConflictingWithDragging = (
  selection: DispatcherDispatchRecord | null,
  dragging: DispatcherOrderRecord,
): boolean => {
  if (!selection) {
    return false;
  }

  if (isNotWarehouseOrder(dragging) && selection.orders.some(isWarehouseOrder)) {
    return true;
  }

  return (
    isWarehouseOrder(dragging) &&
    (selection.orders.some(isNotWarehouseOrder) ||
      selection.orders.some((order) => order.address?.id !== dragging.address?.id))
  );
};

const getWarehouseForOrder = (
  order: DispatcherOrderRecord | undefined,
  region: DispatcherRegionConfig,
  warehouses: DispatcherResources['warehouses'],
) => {
  const targetWarehouseId =
    order?.address?.addressable_type !== 'Warehouse' ? region.default_warehouse_id : order?.address?.addressable_id;

  return warehouses.find((warehouse) => warehouse.id === targetWarehouseId);
};

export const createEmptyDispatch = ({
  region,
  resources,
  dragging,
}: {
  region: DispatcherRegionConfig;
  resources: DispatcherResources;
  dragging?: DispatcherOrderRecord | null;
}): DispatcherDispatchRecord | null => {
  if (dragging && !dragging.estimated_duration && dragging.type !== 'restock' && dragging.type !== 'disposal') {
    return null;
  }

  const warehouse = getWarehouseForOrder(dragging ?? undefined, region, resources.warehouses);
  const depot = resources.depots.find((entry) => entry.id === region.default_depot_id);

  return {
    id: null,
    mapID: UUID(),
    orders: [],
    assignments: [],
    vehicles: [],
    vehicle_preferences: [],
    addons: [],
    groupings: [],
    region,
    warehouse,
    depot,
    auto_dispatch_enabled: true,
  };
};

const updateDispatchArrivalTimes = (dispatches: DispatcherDispatchRecord[]) => {
  dispatches.forEach((dispatch) => {
    if (dispatch._destroy) {
      return;
    }

    if (dispatch.orders.length > 0) {
      const arrival = dispatch.orders.reduce((earliest, order) => {
        const scheduled = new Date(order.scheduled);
        return scheduled < earliest ? scheduled : earliest;
      }, new Date(dispatch.orders[0].scheduled));

      if (!dispatch.arrival) {
        dispatch.arrival = moment(arrival)
          .subtract(DISPATCHER_CONSTANTS.TRAVEL_WINDOW_DURATION, DISPATCHER_CONSTANTS.TRAVEL_WINDOW_UNITS)
          .toDate();
        dispatch.break_at = moment(dispatch.arrival)
          .add(DISPATCHER_CONSTANTS.BREAK_OFFSET, DISPATCHER_CONSTANTS.BREAK_OFFSET_UNITS)
          .toDate();
      }
    } else {
      delete dispatch.arrival;
      delete dispatch.break_at;
    }
  });
};

export const applyOrderDrop = ({
  editorData,
  selection,
  dragging,
  draggingDispatchOffset,
  routeLockActive = true,
  manageMode = false,
}: {
  editorData: DispatcherData;
  selection: DispatcherDispatchRecord | null;
  dragging: DispatcherOrderRecord;
  draggingDispatchOffset: number;
  routeLockActive?: boolean;
  manageMode?: boolean;
}): { error?: string; data?: DispatcherData } => {
  if (routeLockActive && !manageMode && selection?.id && selection.auto_dispatch_enabled !== false) {
    return {};
  }

  if (isSelectionConflictingWithDragging(selection, dragging)) {
    const mixedError = isWarehouseOrder(dragging)
      ? selection?.orders.some(isNotWarehouseOrder)
      : selection?.orders.some(isWarehouseOrder);

    return {
      error: mixedError
        ? "Orders booked at the warehouse can't be dispatched with orders in the field."
        : "Orders booked at different warehouses can't be dispatched together.",
    };
  }

  const attachedChildren = editorData.nonRestockChildOrders.filter((order) => order.parent_id === dragging.id);

  if (!dragging.estimated_duration && dragging.type !== 'restock' && dragging.type !== 'disposal') {
    return { error: MISSING_ESTIMATED_DURATION_MESSAGE };
  }

  dragging.dispatch_offset = draggingDispatchOffset || 0;
  attachedChildren.forEach((child) => {
    child.dispatch_offset = dragging.dispatch_offset ?? 0;
  });

  editorData.dispatches.forEach((dispatch) => {
    dispatch.orders = dispatch.orders.filter(
      (order) => order !== dragging && (order.parent_id !== dragging.id || order.type === 'restock'),
    );
  });

  if (selection) {
    selection.orders.push(dragging);
    attachedChildren.forEach((child) => {
      selection.orders.push(child);
    });
  }

  updateDispatchArrivalTimes(editorData.dispatches);
  updateDispatchGroupings(editorData.dispatches);

  return {
    data: {
      ...editorData,
      dispatches: [...editorData.dispatches],
      orders: [...editorData.orders],
      fieldShiftAssignments: [...editorData.fieldShiftAssignments],
      nonRestockChildOrders: [...editorData.nonRestockChildOrders],
    },
  };
};

export const defaultDropZoneStyle = (
  order: DispatcherOrderRecord,
  display: 'block' | 'none',
): DispatcherDropZoneStyle => {
  const position = positionForOrder(
    { ...order, sla_window_size: order.sla_window_size ?? 1, dispatch_offset: order.dispatch_offset ?? 0 },
    { padded: true },
  );

  return {
    top: toPercentageStyle(position.top),
    bottom: toPercentageStyle(position.bottom),
    height: toPercentageStyle(position.bottom - position.top),
    display,
  };
};

export const highlightDropZoneStyle = (
  order: DispatcherOrderRecord,
  draggingDispatchOffset: number,
): DispatcherDropZoneStyle => {
  const position = positionForOrder(
    { ...order, sla_window_size: order.sla_window_size ?? 1, dispatch_offset: order.dispatch_offset ?? 0 },
    { offset: draggingDispatchOffset },
  );

  return {
    top: toPercentageStyle(position.top),
    bottom: toPercentageStyle(position.bottom),
    height: toPercentageStyle(position.bottom - position.top),
    display: 'block',
  };
};

export const computeDragDispatchOffset = (
  order: DispatcherOrderRecord,
  clientY: number,
  mainElement: HTMLElement,
  dropzoneElement: HTMLElement,
): number => {
  const mainBoundingClientRect = mainElement.getBoundingClientRect();
  const dropzoneBoundingClientRect = dropzoneElement.getBoundingClientRect();
  const { height } = mainBoundingClientRect;
  const dropzoneCenterY = (dropzoneBoundingClientRect.top + dropzoneBoundingClientRect.bottom) * 0.5;
  const units = height / (DISPATCHER_CONSTANTS.DEFAULT_TIMES_TILL - DISPATCHER_CONSTANTS.DEFAULT_TIMES_FROM);
  const delta = ((clientY - dropzoneCenterY) / units) * 3600;

  let draggingDispatchOffset = Math.max(
    0,
    Math.round(delta / DISPATCHER_CONSTANTS.DISPATCH_OFFSET_INTERVAL) * DISPATCHER_CONSTANTS.DISPATCH_OFFSET_INTERVAL,
  );

  const displayWindowInSeconds = (order.sla_window_size ?? 1) * DISPATCHER_CONSTANTS.DEFAULT_WINDOW_SLOT_DURATION;
  if (draggingDispatchOffset > displayWindowInSeconds) {
    draggingDispatchOffset = displayWindowInSeconds;
  }

  return draggingDispatchOffset;
};

export const dispatchLayoutRefresh = () => {
  document.dispatchEvent(new CustomEvent(DISPATCHER_LAYOUT_REFRESH_EVENT));
};

export const createThrottledDragMove = (handler: (order: DispatcherOrderRecord, event: DragEvent) => void) =>
  throttle(handler, 400);
